// src/App.jsx
import React from "react";
import { ApprovalNotificationProvider } from "./context/ApprovalNotificationContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { LeadProvider } from "./context/LeadContext";
import { AttendanceProvider } from "./context/AttendanceContext";
import { TaskProvider } from "./context/TaskContext";
import { MeetingProvider } from "./context/MeetingContext";
import { LeaveProvider } from "./context/LeaveContext";
import { NotesProvider } from "./context/NotesContext";
import { QueryProvider } from "./context/QueryContext";
import { NotificationProvider } from "./context/NotificationContext";
import ProtectedRoute, {
  getDashboardPath,
} from "./components/common/ProtectedRoute";
import { useAuth } from "./context/AuthContext";

import LoginPage from "./pages/auth/LoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminEmployees from "./pages/admin/AdminEmployees";
import AdminAttendance from "./pages/admin/AdminAttendance";
import AdminTasks from "./pages/admin/AdminTasks";
import AdminLeave from "./pages/admin/AdminLeave";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminHolidays from "./pages/admin/AdminHolidays";
import AdminShifts from "./pages/admin/AdminShifts";
import { AdminMeetings } from "./pages/admin/AdminMeetings";
import AdminExtraBreaks from "./pages/admin/AdminExtraBreaks";
import AdminApprovals from "./pages/admin/AdminApprovals";

import LeadDashboard from "./pages/lead/LeadDashboard";
import LeadMeetings from "./pages/lead/LeadMeetings";
import LeadTeam from "./pages/lead/LeadTeam";
import LeadTasks from "./pages/lead/LeadTasks";
import LeadShifts from "./pages/lead/LeadShifts";
import LeadQueries from "./pages/lead/LeadQueries";
import LeadNotes from "./pages/lead/LeadNotes";
import { LeadAttendance } from "./pages/lead/LeadAttendance";
import LeadAnnouncements from "./pages/lead/LeadAnnouncements";
import LeadLeave from "./pages/lead/LeadLeave";
import LeadAnalytics from "./pages/lead/LeadAnalytics";
import LeadExtraBreaks from "./pages/lead/LeadExtraBreaks";
import LeadRecruitment from "./pages/lead/LeadRecruitment";

import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import EmployeeTasks from "./pages/employee/EmployeeTasks";
import EmployeeLeave from "./pages/employee/EmployeeLeave";
import EmployeeNotes from "./pages/employee/EmployeeNotes";
import EmployeeQueries from "./pages/employee/EmployeeQueries";
import {
  EmployeeMeetings,
  EmployeeAnnouncements,
} from "./pages/employee/EmployeeMeetings";
import MyAttendance from "./pages/common/MyAttendance";

import TaskBoard from "./pages/TaskBoard";


import NotificationManager from "./components/notifications/NotificationManager";
import TravelReimbursement from "./pages/employee/TravelReimbursement";
import TravelReimbursementNew from "./pages/employee/TravelReimbursementNew";
import EmployeeProfile from "./pages/employee/EmployeeProfile";
import EmployeeJobBoard from "./pages/employee/EmployeeJobBoard";

// ── HR Module Pages ────────────────────────────────────────
import HRDashboard from "./pages/hr/HRDashboard";
import HRRecruitment from "./pages/hr/HRRecruitment";
import HROnboarding from "./pages/hr/HROnboarding";
import HREmployees from "./pages/hr/HREmployees";
import HRWorkforce from "./pages/hr/HRWorkforce";
import HRPerformance from "./pages/hr/HRPerformance";
import HRLearning from "./pages/hr/HRLearning";
import HRExpenses from "./pages/hr/HRExpenses";
import HRAssets from "./pages/hr/HRAssets";
import HRTravel from "./pages/hr/HRTravel";
import HREmployeeServices from "./pages/hr/HREmployeeServices";
import HROffboarding from "./pages/hr/HROffboarding";
import HRReports from "./pages/hr/HRReports";
import HRCompliance from "./pages/hr/HRCompliance";
import HRAdministration from "./pages/hr/HRAdministration";
import HRAIAssistant from "./pages/hr/HRAIAssistant";

import FinanceDashboard from "./pages/finance/FinanceDashboard";
import PayrollDashboard from "./pages/finance/payroll/PayrollDashboard";
import PayrollProcessing from "./pages/finance/payroll/PayrollProcessing";
import PayrollApproval from "./pages/finance/payroll/PayrollApproval";
import PayrollReports from "./pages/finance/payroll/PayrollReports";
import Payslips from "./pages/finance/payroll/Payslips";
import SalaryStructure from "./pages/finance/payroll/SalaryStructure";
import SalaryComponents from "./pages/finance/payroll/SalaryComponents";
import SalaryRegister from "./pages/finance/payroll/SalaryRegister";
import EmployeeSalaryAssignment from "./pages/finance/payroll/EmployeeSalaryAssignment";

function RootRedirect() {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Initialisingâ€¦</p>
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!userProfile) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Loading your profileâ€¦</p>
      </div>
    );
  }
  return <Navigate to={getDashboardPath(userProfile.role)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        
        <NotesProvider>
          <QueryProvider>
            <LeaveProvider>
               <ApprovalNotificationProvider>
              <AttendanceProvider>
                <TaskProvider>
                  <MeetingProvider>
                    <BrowserRouter>
                    <NotificationManager />
                      <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<LoginPage />} />

                        {/* ── Admin ──────────────────────────────── */}
                        <Route
                          path="/admin/dashboard"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/employees"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminEmployees />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/attendance"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAttendance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/my-attendance"
                          element={
                            <ProtectedRoute role="admin">
                              <MyAttendance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/tasks"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminTasks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/taskboard"
                          element={
                            <ProtectedRoute role="admin">
                              <TaskBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/meetings"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminMeetings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/announcements"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAnnouncements />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/leave"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminLeave />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/holidays"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminHolidays />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/shifts"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminShifts />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/reports"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminAnalytics />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/settings"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/extra-breaks"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminExtraBreaks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/approvals"
                          element={
                            <ProtectedRoute role="admin">
                              <AdminApprovals />
                            </ProtectedRoute>
                          }
                        />

                        {/* ── Lead ─────────────────────────────────── */}
                        <Route
                          path="/lead/*"
                          element={
                            <ProtectedRoute role="lead">
                              <LeadProvider>
                                <Routes>
                                  <Route
                                    path="dashboard"
                                    element={<LeadDashboard />}
                                  />
                                  <Route
                                    path="attendance"
                                    element={<LeadAttendance />}
                                  />
                                  <Route path="reports" element={<LeadAnalytics />} />
                                  <Route
                                    path="my-attendance"
                                    element={<MyAttendance />}
                                  />
                                  <Route path="team" element={<LeadTeam />} />
                                  <Route path="tasks" element={<LeadTasks />} />
                                  <Route path="taskboard" element={<TaskBoard />} />
                                  <Route path="meetings" element={<LeadMeetings />} />
                                  <Route path="leave" element={<LeadLeave />} />
                                  <Route path="shifts" element={<LeadShifts />} />
                                  <Route
                                    path="announcements"
                                    element={<LeadAnnouncements />}
                                  />
                                  <Route
                                    path="extra-breaks"
                                    element={<LeadExtraBreaks />}
                                  />
                                  <Route
                                    path="queries"
                                    element={<LeadQueries />}
                                  />
                                  <Route
                                    path="notes"
                                    element={<LeadNotes />}
                                  />
                                  <Route
                                    path="extra-breaks"
                                    element={<LeadExtraBreaks />}
                                  />
                                  <Route
                                    path="recruitment"
                                    element={<LeadRecruitment />}
                                  />
                                </Routes>
                              </LeadProvider>
                            </ProtectedRoute>
                          }
                        />



                        {/* ── Employee ─────────────────────────────── */}
                        <Route
                          path="/employee/dashboard"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/my-attendance"
                          element={
                            <ProtectedRoute role="employee">
                              <MyAttendance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/tasks"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeTasks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/taskboard"
                          element={
                            <ProtectedRoute role="employee">
                              <TaskBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/meetings"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeMeetings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/leave"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeLeave />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/announcements"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeAnnouncements />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/notes"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeNotes />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/queries"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeQueries />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/jobs"
                          element={
                            <ProtectedRoute role="employee">
                              <EmployeeJobBoard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/employee/profile"
                          element={
                            <ProtectedRoute role={["employee", "lead", "admin", "hr"]}>
                              <EmployeeProfile />
                            </ProtectedRoute>
                          }
                        />

                        {/* ── HR Module ─────────────────────────────── */}
                        <Route
                          path="/hr/*"
                          element={
                            <ProtectedRoute role={["hr", "admin"]}>
                              <Routes>
                                <Route path="dashboard" element={<HRDashboard />} />
                                <Route path="recruitment" element={<HRRecruitment />} />
                                <Route path="onboarding" element={<HROnboarding />} />
                                <Route path="employees" element={<HREmployees />} />
                                <Route path="workforce" element={<HRWorkforce />} />
                                <Route path="performance" element={<HRPerformance />} />
                                <Route path="learning" element={<HRLearning />} />
                                <Route path="expenses" element={<HRExpenses />} />
                                <Route path="assets" element={<HRAssets />} />
                                <Route path="travel" element={<HRTravel />} />
                                <Route path="services" element={<HREmployeeServices />} />
                                <Route path="offboarding" element={<HROffboarding />} />
                                <Route path="reports" element={<HRReports />} />
                                <Route path="compliance" element={<HRCompliance />} />
                                <Route path="administration" element={<HRAdministration />} />
                                <Route path="ai-assistant" element={<HRAIAssistant />} />
                              </Routes>
                            </ProtectedRoute>
                          }
                        />


                        <Route
                          path="/travel-reimbursement"
                          element={
                            <ProtectedRoute>
                              <TravelReimbursement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/travel-reimbursement/new"
                          element={
                            <ProtectedRoute>
                              <TravelReimbursementNew />
                            </ProtectedRoute>
                          }
                        />
                        <Route
  path="/finance/*"
  element={
    <ProtectedRoute role={["finance", "admin"]}>
      <Routes>
        <Route path="dashboard" element={<FinanceDashboard />} />
        <Route path="payroll/dashboard" element={<PayrollDashboard />} />
        <Route path="payroll/processing" element={<PayrollProcessing />} />
        <Route path="payroll/approval" element={<PayrollApproval />} />
        <Route path="payroll/reports" element={<PayrollReports />} />
        <Route path="payroll/payslips" element={<Payslips />} />
        <Route path="payroll/salary-structure" element={<SalaryStructure />} />
        <Route path="payroll/salary-components" element={<SalaryComponents />} />
        <Route path="payroll/salary-register" element={<SalaryRegister />} />
        <Route path="payroll/assign-salary" element={<EmployeeSalaryAssignment />} />
      </Routes>
    </ProtectedRoute>
  }
/>
                        {/* â”€â”€ Catch-all fallback for unmapped routes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
                        <Route
                          path="*"
                          element={<Navigate to="/login" replace />}
                        />
                      </Routes>
                    </BrowserRouter>
                  </MeetingProvider>
                </TaskProvider>
              </AttendanceProvider>
              </ApprovalNotificationProvider>
            </LeaveProvider>
          </QueryProvider>
        </NotesProvider>
         
      </NotificationProvider>
    </AuthProvider>
  );
}
